import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, ListItemText, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import * as Yup from "yup";
import { ID_ACCOUNTED, ID_SENT, ID_TO_SEND, ID_UNACCOUNTED, } from "../constants/PayableInvoicesStatuses";
import {
    ID_FILTER_CUSTOMER,
    ID_FILTER_CUSTOMER_COMPANY_NAME,
    ID_FILTER_CUSTOMER_ID,
    ID_FILTER_INVOICEDATEFROM,
    ID_FILTER_INVOICEDATETO,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUS,
    ID_FILTER_DEALERS,
    ID_FILTER_LEGAL_ENTITIES,
    payableInvoiceDefaultValues
} from "../constants/PayableInvoicesFilters";
import { addFilter, getFilterValue } from "../../../utils/FilterUtils";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS } from "../constants/PayableInvoicesRecoil";
import { COLOR_ACCOUNTED, COLOR_SENT, COLOR_TO_SEND, } from "../constants/PayableInvoicesColors";
import ColouredDot from "../../../component/ColouredDot";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function PassiveInvoicesFilter() {

    const { t } = useTranslation("finance", { keyPrefix: "invoices" });
    const { t: tg } = useTranslation("translation");
    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [search, setSearch] = useState("");
    // Use Context
    const {
      globalData: { user },
    } = React.useContext(GlobalContext);
     // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        })
        );
    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
          value: x.name,
          label: tg(`dealers.${x.name}`),
      }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        customerId: Yup.string().max(128, t("filters.errors.invalid-length")),
        customer: Yup.string().max(128, t("filters.errors.invalid-length")),
        customerCompanyName: Yup.string().max(
                128,
                t("filters.errors.invalid-length")
        ),
        statuses: Yup.array(),
        invoiceDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.invoiceDateTo || (value <= context.parent.invoiceDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        invoiceDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.invoiceDateFrom || (value >= context.parent.invoiceDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne')),
    });

    const defaultValues = {
        operator: "",
        customerId: getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs) || "",
        customer: getFilterValue(ID_FILTER_CUSTOMER, filterDefs) || "",
        customerCompanyName: getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs) || "",
        statuses: getFilterValue(ID_FILTER_STATUS, filterDefs) || [],
        invoiceDateFrom: getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs) || null,
        invoiceDateTo: getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs) || null,
        legalEntities:
             getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
             legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };

    const renderCustomer = (field: any) => (
            <TextField
                    fullWidth
                    name="customer"
                    label={t("filters.payable-invoices." + ID_FILTER_CUSTOMER)}
                    value={field.value}
                    onChange={field.onChange}
            ></TextField>
    );

    const renderCustomerId = (field: any) => (
            <TextField
                    fullWidth
                    name="customerId"
                    label={t("filters.payable-invoices." + ID_FILTER_CUSTOMER_ID)}
                    value={field.value}
                    onChange={field.onChange}
            ></TextField>
    );

    const renderCustomerCompanyName = (field: any) => (
            <TextField
                    fullWidth
                    name="customerCompanyName"
                    label={t("filters.invoices." + ID_FILTER_CUSTOMER_COMPANY_NAME)}
                    value={field.value}
                    onChange={field.onChange}
            ></TextField>
    );

    const renderInvoiceDateFrom = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    label={t("filters.invoices." + ID_FILTER_INVOICEDATEFROM)}
                    views={["day"]}
                    value={field.value}
                    maxDate={
                        values["invoiceDateTo"] !== null ? values["invoiceDateTo"] : null
                    }
                    onChange={field.onChange}
                    renderInput={(params) => (
                            <TextField
                                    {...params}
                                    fullWidth
                                    id={"invoiceDateFrom"}
                                    error={error !== undefined}
                                    helperText={error ? error.message : ""}
                            />
                    )}
                    inputFormat={"L"}
            />
    );

    const renderInvoiceDateTo = (field: any, error: any, state: any, values: any) => (
            <DatePicker
                    label={t("filters.invoices." + ID_FILTER_INVOICEDATETO)}
                    views={["day"]}
                    value={field.value}
                    minDate={values["invoiceDateFrom"] !== null ? values["invoiceDateFrom"] : null}
                    onChange={field.onChange}
                    renderInput={(params) => (
                            <TextField {...params} fullWidth
                                       id={"invoiceDateTo"}
                                       error={error !== undefined}
                                       helperText={error ? error.message : ""}
                            />
                    )}
                    inputFormat={"L"}
            />
    );

    const renderStatuses = (field: any) => (
            <TextField fullWidth
                       select
                       SelectProps={{
                           multiple: true,
                           renderValue: (selected: any) =>
                                   selected
                                           .map((s: any) => statuses.find((ss) => ss.value === s)!.label)
                                           .join(", "),
                       }}
                       label={t("filters.invoices." + ID_FILTER_STATUS)}
                       value={field.value}
                       onChange={field.onChange}
            >
                {statuses.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                            <ColouredDot color={status?.color}/>
                            <ListItemText primary={status.label}/>
                        </MenuItem>
                ))}
            </TextField>
    );
    const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
        return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
          <TextField
            name={name}
            fullWidth
            select
            hidden={isHidden}
            disabled={list.length==0}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) =>
                selected
                  .map((s: any) => list.find((ss) => ss.value === s)!.label)
                  .join(", "),
            }}
            label={label}
            value={field.value}
            onChange={(event) => {
              if (onChange) {
                onChange(event.target.value, setValue);
              }
              field.onChange(event);
            }}
            error={error !== undefined}
            helperText={error ? error.message : ""}
          >
            {list.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                <Checkbox checked={field.value?.indexOf(item.value) > -1} />
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </TextField>
        );
   };

    const fields = {
        customerId: renderCustomerId,
        customer: renderCustomer,
        customerCompanyName: renderCustomerCompanyName,
        statuses: renderStatuses,
        invoiceDateFrom: renderInvoiceDateFrom,
        invoiceDateTo: renderInvoiceDateTo,
        legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                        setValue('dealers', []);
                        let dealers = user?.dealers
                        ?.filter(
                        (x: any) =>
                        value?.length > 0 &&
                        x.legalEntities.some((e: any) => value.includes(e.name)) &&
                        x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                        return {
                        label: tg(`dealers.${x.name}`),
                        value: x.name,
                        };
                        });
                        setDealers(dealers);
                }
                ),
                dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)
        };

    const statuses = [
        {
            value: ID_TO_SEND,
            label: t("statuses." + ID_TO_SEND),
            color: COLOR_TO_SEND + ".transparent",
        },
        {
            value: ID_SENT,
            label: t("statuses." + ID_SENT),
            color: COLOR_SENT + ".transparent",
        },
        {
            value: ID_ACCOUNTED,
            label: t("statuses." + ID_ACCOUNTED),
            color: COLOR_ACCOUNTED + ".transparent",
        },
        {
            value: ID_UNACCOUNTED,
            label: t("statuses." + ID_UNACCOUNTED),
            color: "neutral.light",
        }
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_CUSTOMER, data.customer);
        addFilter(setFilters, ID_FILTER_CUSTOMER_ID, data.customerId);
        addFilter(setFilters, ID_FILTER_CUSTOMER_COMPANY_NAME, data.customerCompanyName);
        addFilter(setFilters, ID_FILTER_INVOICEDATEFROM, asStartOfDay(data.invoiceDateFrom));
        addFilter(setFilters, ID_FILTER_INVOICEDATETO, asEndOfDay(data.invoiceDateTo));
        addFilter(setFilters, ID_FILTER_STATUS, data.statuses);
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);
        setDealers(dealerOptions);
    };

    return (
            <AdvancedFilter
                    schema={schema}
                    fields={fields}
                    defaultValues={defaultValues}
                    onSubmit={submit}
                    onReset={reset}
                    searchInput={search}
                    setSearchInput={setSearch}
                    customDefaultValues={payableInvoiceDefaultValues}
            />
    );
}

export default PassiveInvoicesFilter;